import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Target,
  Calendar,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Clock,
  Eye
} from 'lucide-react'
import {
  DashboardMetrics,
  FilterState,
  LeadMatch,
  ConfidenceLevel,
  Lead as LeadAttr,
  JobberRequest
} from '@/types'
import { getConfidenceLevel } from '@/lib/lead-matching'
import { supabase } from '@/lib/supabase'

// Removed N8N API calls - UI should integrate with Supabase only
// N8N API is for data sync into Supabase, not direct UI calls

// Enhanced interfaces for pagination and analytics
interface PaginationState {
  page: number
  pageSize: number
  total: number
}

interface TimeAnalytics {
  weekly: { week: string; count: number; value: number }[]
  monthly: { month: string; count: number; value: number }[]
  yearly: { year: string; count: number; value: number }[]
}

interface FranchiseAnalytics {
  franchise: string
  totalRequests: number
  totalValue: number
  avgValue: number
  conversionRate: number
  timeAnalytics: TimeAnalytics
}

interface CityAnalytics {
  city: string
  province: string
  postalCode: string
  totalRequests: number
  totalValue: number
  franchise: string
  timeAnalytics: TimeAnalytics
}

// Real data via working edge function
const fetchDashboardMetrics = async (filters: FilterState): Promise<DashboardMetrics> => {
  try {
    console.log('Fetching dashboard metrics with filters:', filters)
    
    const { data, error } = await supabase.functions.invoke('get-dashboard-metrics', {
      body: {
        franchise: filters.franchise,
        dateRange: filters.dateRange ? {
          start: filters.dateRange.start.toISOString(),
          end: filters.dateRange.end.toISOString()
        } : undefined,
        channel: filters.channel
      }
    })
    
    if (error) {
      console.error('Edge function error:', error)
      throw error
    }
    
    console.log('Real dashboard data received:', data)
    
    return data.metrics || {
      summary: {
        totalLeads: 0,
        totalRequests: 0,
        matchedRequests: 0,
        unmatchedRequests: 0,
        convertedJobs: 0,
        totalRevenue: 0,
        matchRate: '0%',
        conversionRate: '0%',
        avgConversionDays: '0'
      },
      byChannel: {},
      byFranchise: {},
      byLocation: {}
    }
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error)
    
    // Show error state, not mock data
    throw error
  }
}

const fetchJobberRequests = async (filters: FilterState, pagination?: PaginationState) => {
  try {
    console.log('Fetching Jobber requests with filters:', filters, 'pagination:', pagination)
    
    // Base query for count
    let countQuery = supabase
      .from('requests')
      .select('*', { count: 'exact', head: true })
    
    // Base query for data
    let query = supabase
      .from('requests')
      .select(`
        id,
        jobber_request_id,
        client_name,
        client_email,
        client_phone,
        request_title,
        franchise,
        status,
        jobber_created_at,
        service_city,
        service_province,
        service_zip,
        source,
        service_street
      `)
      .order('jobber_created_at', { ascending: false })
    
    // Apply filters to both queries
    if (filters.franchise && filters.franchise !== 'all') {
      countQuery = countQuery.eq('franchise', filters.franchise)
      query = query.eq('franchise', filters.franchise)
    }
    
    if (filters.dateRange) {
      const startDate = filters.dateRange.start.toISOString()
      const endDate = filters.dateRange.end.toISOString()
      countQuery = countQuery.gte('jobber_created_at', startDate).lte('jobber_created_at', endDate)
      query = query.gte('jobber_created_at', startDate).lte('jobber_created_at', endDate)
    }
    
    // Apply time frame filter
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date()
      let startDate: Date
      
      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          break
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1)
          break
        default:
          startDate = new Date(0)
      }
      
      const startDateISO = startDate.toISOString()
      countQuery = countQuery.gte('jobber_created_at', startDateISO)
      query = query.gte('jobber_created_at', startDateISO)
    }
    
    // Apply city filter
    if (filters.city) {
      countQuery = countQuery.ilike('service_city', `%${filters.city}%`)
      query = query.ilike('service_city', `%${filters.city}%`)
    }
    
    // Apply source filter
    if (filters.source) {
      countQuery = countQuery.ilike('source', `%${filters.source}%`)
      query = query.ilike('source', `%${filters.source}%`)
    }
    
    // Apply pagination
    if (pagination) {
      const from = (pagination.page - 1) * pagination.pageSize
      const to = from + pagination.pageSize - 1
      query = query.range(from, to)
    }
    
    // Get total count
    const { count, error: countError } = await countQuery
    if (countError) throw countError
    
    // Get data
    const { data, error } = await query
    if (error) throw error
    
    console.log(`Real Jobber requests received: ${data?.length} records of ${count} total`)
    return { data: data || [], total: count || 0 }
  } catch (error) {
    console.error('Error fetching Jobber requests:', error)
    throw error
  }
}

const fetchWebsiteLeads = async (filters: FilterState, pagination?: PaginationState) => {
  try {
    console.log('Fetching website leads with filters:', filters, 'pagination:', pagination)
    
    // Base query for count
    let countQuery = supabase
      .from('leads')
      .select('*', { count: 'exact', head: true })
    
    // Base query for data
    let query = supabase
      .from('leads')
      .select(`
        id,
        client_name,
        client_email,
        client_phone,
        service_street,
        service_requested,
        service_slug,
        message,
        status,
        submission_url,
        created_at
      `)
      .order('created_at', { ascending: false })
    
    // Apply filters to both queries
    if (filters.dateRange) {
      const startDate = filters.dateRange.start.toISOString()
      const endDate = filters.dateRange.end.toISOString()
      countQuery = countQuery.gte('created_at', startDate).lte('created_at', endDate)
      query = query.gte('created_at', startDate).lte('created_at', endDate)
    }
    
    // Apply time frame filter
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date()
      let startDate: Date
      
      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          break
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1)
          break
        default:
          startDate = new Date(0)
      }
      
      const startDateISO = startDate.toISOString()
      countQuery = countQuery.gte('created_at', startDateISO)
      query = query.gte('created_at', startDateISO)
    }
    
    // Apply service requested filter (similar to source)
    if (filters.source) {
      countQuery = countQuery.ilike('service_requested', `%${filters.source}%`)
      query = query.ilike('service_requested', `%${filters.source}%`)
    }
    
    // Apply pagination
    if (pagination) {
      const from = (pagination.page - 1) * pagination.pageSize
      const to = from + pagination.pageSize - 1
      query = query.range(from, to)
    }
    
    // Get total count
    const { count, error: countError } = await countQuery
    if (countError) throw countError
    
    // Get data
    const { data, error } = await query
    if (error) throw error
    
    console.log(`Real website leads received: ${data?.length} records of ${count} total`)
    return { data: data || [], total: count || 0 }
  } catch (error) {
    console.error('Error fetching website leads:', error)
    throw error
  }
}

const fetchLeadMatches = async (filters: FilterState): Promise<LeadMatch[]> => {
  try {
    console.log('Fetching lead matches with filters:', filters)
    
    // Direct database query without joins to avoid relationship errors
    let query = supabase
      .from('lead_request_matches')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (filters.franchise && filters.franchise !== 'all') {
      query = query.eq('franchise', filters.franchise)
    }
    
    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'matched') {
        query = query.neq('match_type', 'none')
      } else if (filters.status === 'unmatched') {
        query = query.eq('match_type', 'none')
      }
    }
    
    if (filters.dateRange) {
      query = query
        .gte('created_at', filters.dateRange.start.toISOString())
        .lte('created_at', filters.dateRange.end.toISOString())
    }
    
    const { data, error } = await query
    
    if (error) {
      console.error('Database query error:', error)
      throw error
    }
    
    console.log('Real lead matches received:', data?.length, 'records')
    
    // Transform the real data to match our LeadMatch interface
    return (data || []).map(match => ({
      ...match,
      leads: match.lead_id ? {
        id: match.lead_id,
        client_name: `Jobber Request ${match.jobber_request_id.slice(-6)}`, // Fallback name
        client_email: null,
        client_phone: null,
        requested_service: 'Unknown',
        street_address: match.match_details?.requestData?.address ? 
          `${match.match_details.requestData.address.street1}, ${match.match_details.requestData.address.city} ${match.match_details.requestData.address.province}` : 
          'Unknown',
        created_at: match.request_created_at || match.created_at
      } : null
    }))
  } catch (error) {
    console.error('Error fetching lead matches:', error)
    throw error
  }
}

// Franchise analytics using Supabase data
const fetchFranchiseAnalytics = async (filters: FilterState): Promise<FranchiseAnalytics[]> => {
  try {
    console.log('🔄 Fetching franchise analytics from Supabase...')
    
    // Base query for all requests
    let query = supabase
      .from('requests')
      .select(`
        franchise,
        status,
        jobber_created_at,
        service_city,
        service_province
      `)
    
    // Apply filters
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date()
      let startDate: Date
      
      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          break
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1)
          break
        default:
          startDate = new Date(0)
      }
      
      query = query.gte('jobber_created_at', startDate.toISOString())
    }
    
    if (filters.city) {
      query = query.ilike('service_city', `%${filters.city}%`)
    }
    
    const { data: requests, error } = await query
    
    if (error) throw error
    
    // Group by franchise and calculate analytics
    const franchiseMap = new Map<string, any>()
    
    requests?.forEach(request => {
      const franchise = request.franchise || 'unknown'
      
      if (!franchiseMap.has(franchise)) {
        franchiseMap.set(franchise, {
          franchise,
          requests: [],
          totalValue: 0,
          conversions: 0
        })
      }
      
      const franchiseData = franchiseMap.get(franchise)
      franchiseData.requests.push(request)
      
      if (request.status === 'completed' || request.status === 'converted') {
        franchiseData.conversions++
        franchiseData.totalValue += 1 // Count successful requests
      }
    })
    
    // Convert to analytics format
    return Array.from(franchiseMap.values()).map(franchise => ({
      franchise: franchise.franchise,
      totalRequests: franchise.requests.length,
      totalValue: franchise.totalValue,
      avgValue: franchise.totalValue / franchise.requests.length || 0,
      conversionRate: (franchise.conversions / franchise.requests.length * 100) || 0,
      timeAnalytics: calculateTimeAnalytics(franchise.requests)
    }))
  } catch (error) {
    console.error('Error fetching franchise analytics:', error)
    throw error
  }
}

const fetchCityAnalytics = async (filters: FilterState): Promise<CityAnalytics[]> => {
  try {
    console.log('🔄 Fetching city analytics from Supabase...')
    
    // Base query for all requests with city information
    let query = supabase
      .from('requests')
      .select(`
        franchise,
        status,
        jobber_created_at,
        service_city,
        service_province,
        service_zip
      `)
      .not('service_city', 'is', null)
    
    // Apply filters
    if (filters.franchise && filters.franchise !== 'all') {
      query = query.eq('franchise', filters.franchise)
    }
    
    if (filters.timeFrame && filters.timeFrame !== 'all') {
      const now = new Date()
      let startDate: Date
      
      switch (filters.timeFrame) {
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          break
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          break
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
          break
        case 'thisyear':
          startDate = new Date(now.getFullYear(), 0, 1)
          break
        default:
          startDate = new Date(0)
      }
      
      query = query.gte('jobber_created_at', startDate.toISOString())
    }
    
    if (filters.city) {
      query = query.ilike('service_city', `%${filters.city}%`)
    }
    
    const { data: requests, error } = await query
    
    if (error) throw error
    
    // Group by city and calculate analytics
    const cityMap = new Map<string, any>()
    
    requests?.forEach(request => {
      const city = request.service_city
      const province = request.service_province
      
      if (!city || !province) return
      
      const cityKey = `${city}-${province}`
      if (!cityMap.has(cityKey)) {
        cityMap.set(cityKey, {
          city,
          province,
          postalCode: request.service_zip || '',
          franchise: request.franchise,
          requests: [],
          totalValue: 0
        })
      }
      
      const cityData = cityMap.get(cityKey)
      cityData.requests.push(request)
      if (request.status === 'completed' || request.status === 'converted') {
        cityData.totalValue += 1
      }
    })
    
    // Convert to analytics format
    return Array.from(cityMap.values()).map(city => ({
      city: city.city,
      province: city.province,
      postalCode: city.postalCode,
      totalRequests: city.requests.length,
      totalValue: city.totalValue,
      franchise: city.franchise,
      timeAnalytics: calculateTimeAnalytics(city.requests)
    }))
  } catch (error) {
    console.error('Error fetching city analytics:', error)
    throw error
  }
}

const calculateTimeAnalytics = (requests: any[]): TimeAnalytics => {
  const weekly = new Map<string, { count: number; value: number }>()
  const monthly = new Map<string, { count: number; value: number }>()
  const yearly = new Map<string, { count: number; value: number }>()
  
  requests.forEach(request => {
    const date = new Date(request.jobber_created_at)
    const value = request.job_total || 0
    
    // Weekly analytics (start of week)
    const weekStart = new Date(date)
    weekStart.setDate(date.getDate() - date.getDay())
    const weekKey = weekStart.toISOString().split('T')[0]
    
    if (!weekly.has(weekKey)) {
      weekly.set(weekKey, { count: 0, value: 0 })
    }
    const weekData = weekly.get(weekKey)!
    weekData.count++
    weekData.value += value
    
    // Monthly analytics
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    if (!monthly.has(monthKey)) {
      monthly.set(monthKey, { count: 0, value: 0 })
    }
    const monthData = monthly.get(monthKey)!
    monthData.count++
    monthData.value += value
    
    // Yearly analytics
    const yearKey = String(date.getFullYear())
    if (!yearly.has(yearKey)) {
      yearly.set(yearKey, { count: 0, value: 0 })
    }
    const yearData = yearly.get(yearKey)!
    yearData.count++
    yearData.value += value
  })
  
  return {
    weekly: Array.from(weekly.entries()).map(([week, data]) => ({
      week,
      count: data.count,
      value: data.value
    })).sort((a, b) => a.week.localeCompare(b.week)),
    monthly: Array.from(monthly.entries()).map(([month, data]) => ({
      month,
      count: data.count,
      value: data.value
    })).sort((a, b) => a.month.localeCompare(b.month)),
    yearly: Array.from(yearly.entries()).map(([year, data]) => ({
      year,
      count: data.count,
      value: data.value
    })).sort((a, b) => a.year.localeCompare(b.year))
  }
}

// Removed calculateTimeAnalyticsFromJobber - using unified calculateTimeAnalytics function

interface StatsCardProps {
  title: string
  value: string | number
  subtitle?: string
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: string
  icon: React.ReactNode
  className?: string
}

function StatsCard({ title, value, subtitle, trend, trendValue, icon, className = '' }: StatsCardProps) {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-500" />
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-500" />
    return null
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && (
          <div className="flex items-center text-xs text-muted-foreground mt-1">
            {getTrendIcon()}
            <span className={trend === 'up' ? 'text-green-500' : trend === 'down' ? 'text-red-500' : ''}>
              {trendValue && `${trendValue} `}{subtitle}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface ConfidenceBadgeProps {
  score: number
}

function ConfidenceBadge({ score }: ConfidenceBadgeProps) {
  const confidence = getConfidenceLevel(score)
  
  const colors = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-orange-100 text-orange-800',
    none: 'bg-gray-100 text-gray-800'
  }
  
  const icons = {
    high: <CheckCircle className="w-3 h-3 mr-1" />,
    medium: <AlertCircle className="w-3 h-3 mr-1" />,
    low: <Clock className="w-3 h-3 mr-1" />,
    none: <Clock className="w-3 h-3 mr-1" />
  }
  
  return (
    <Badge className={colors[confidence]}>
      {icons[confidence]}
      {confidence.charAt(0).toUpperCase() + confidence.slice(1)} ({score}%)
    </Badge>
  )
}

interface ChannelCardProps {
  name: string
  metrics: {
    leads: number
    requests: number
    jobs: number
    revenue: number
  }
}

function ChannelCard({ name, metrics }: ChannelCardProps) {
  const conversionRate = metrics.requests > 0 ? ((metrics.jobs / metrics.requests) * 100).toFixed(1) : '0'
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Leads</span>
          <span className="font-medium">{metrics.leads}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Requests</span>
          <span className="font-medium">{metrics.requests}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Jobs</span>
          <span className="font-medium">{metrics.jobs}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Revenue</span>
          <span className="font-medium">${metrics.revenue.toLocaleString()}</span>
        </div>
        <div className="pt-2 border-t">
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">Conversion</span>
            <span className="font-medium text-green-600">{conversionRate}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Pagination component
interface PaginationControlsProps {
  pagination: PaginationState
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading?: boolean
}

function PaginationControls({ pagination, onPageChange, onPageSizeChange, isLoading }: PaginationControlsProps) {
  const totalPages = Math.ceil(pagination.total / pagination.pageSize)
  const startRecord = (pagination.page - 1) * pagination.pageSize + 1
  const endRecord = Math.min(pagination.page * pagination.pageSize, pagination.total)

  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex items-center space-x-2">
        <span className="text-sm text-muted-foreground">Rows per page:</span>
        <Select
          value={String(pagination.pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value))}
          disabled={isLoading}
        >
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex items-center space-x-2">
        <span className="text-sm text-muted-foreground">
          {startRecord}-{endRecord} of {pagination.total.toLocaleString()}
        </span>
        
        <div className="flex items-center space-x-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <span className="text-sm font-medium px-2">
            Page {pagination.page} of {totalPages}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= totalPages || isLoading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function LeadAttributionDashboard() {
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    franchise: undefined,
    channel: undefined,
    dateRange: undefined
  })
  
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMatch, setSelectedMatch] = useState<LeadMatch | null>(null)
  const [analyticsSearch, setAnalyticsSearch] = useState('')
  
  // Pagination states
  const [requestsPagination, setRequestsPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0
  })
  
  const [leadsPagination, setLeadsPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0
  })
  
  // Fetch dashboard metrics
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useQuery({
    queryKey: ['dashboard-metrics', filters],
    queryFn: () => fetchDashboardMetrics(filters)
  })
  
  // Fetch Jobber requests with pagination
  const { data: requestsData, isLoading: requestsLoading, refetch: refetchRequests } = useQuery({
    queryKey: ['jobber-requests', filters, requestsPagination],
    queryFn: () => fetchJobberRequests(filters, requestsPagination)
  })

  // Update pagination total when data changes
  useEffect(() => {
    if (requestsData?.total !== undefined) {
      setRequestsPagination(prev => ({ ...prev, total: requestsData.total }))
    }
  }, [requestsData?.total])

  // Fetch website leads with pagination
  const { data: leadsData, isLoading: leadsLoading, refetch: refetchLeads } = useQuery({
    queryKey: ['website-leads', filters, leadsPagination],
    queryFn: () => fetchWebsiteLeads(filters, leadsPagination)
  })

  // Update pagination total when data changes
  useEffect(() => {
    if (leadsData?.total !== undefined) {
      setLeadsPagination(prev => ({ ...prev, total: leadsData.total }))
    }
  }, [leadsData?.total])

  // Fetch lead matches
  const { data: matches = [], isLoading: matchesLoading, refetch: refetchMatches } = useQuery({
    queryKey: ['lead-matches', filters, searchTerm],
    queryFn: () => fetchLeadMatches(filters)
  })

  // Fetch analytics data
  const { data: franchiseAnalytics = [], isLoading: franchiseAnalyticsLoading } = useQuery({
    queryKey: ['franchise-analytics', filters],
    queryFn: () => fetchFranchiseAnalytics(filters)
  })

  const { data: cityAnalytics = [], isLoading: cityAnalyticsLoading } = useQuery({
    queryKey: ['city-analytics', filters],
    queryFn: () => fetchCityAnalytics(filters)
  })

  // Extract data from paginated responses
  const requests = requestsData ? requestsData.data : []
  const leads = leadsData ? leadsData.data : []
  
  const handleRefresh = () => {
    refetchMetrics()
    refetchRequests()
    refetchLeads()
    refetchMatches()
  }
  
  const filteredMatches = matches.filter(match => {
    if (!searchTerm) return true
    
    const searchLower = searchTerm.toLowerCase()
    return (
      match.leads?.client_name?.toLowerCase().includes(searchLower) ||
      match.leads?.client_email?.toLowerCase().includes(searchLower) ||
      match.leads?.client_phone?.includes(searchTerm) ||
      match.jobber_request_id.toLowerCase().includes(searchLower)
    )
  })

  // Pagination helpers
  const handleRequestsPageChange = (page: number) => {
    setRequestsPagination(prev => ({ ...prev, page }))
  }

  const handleRequestsPageSizeChange = (pageSize: number) => {
    setRequestsPagination(prev => ({ ...prev, pageSize, page: 1 }))
  }

  const handleLeadsPageChange = (page: number) => {
    setLeadsPagination(prev => ({ ...prev, page }))
  }

  const handleLeadsPageSizeChange = (pageSize: number) => {
    setLeadsPagination(prev => ({ ...prev, pageSize, page: 1 }))
  }
  
  if (metricsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lead Attribution Dashboard</h1>
          <p className="text-muted-foreground">
            Track marketing leads through to Jobber requests and job conversions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Leads"
          value={metrics?.summary.totalLeads || 0}
          subtitle="from marketing campaigns"
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
        />
        
        <StatsCard
          title="Jobber Requests"
          value={metrics?.summary.totalRequests || 0}
          subtitle="service requests"
          icon={<Phone className="h-4 w-4 text-muted-foreground" />}
        />
        
        <StatsCard
          title="Match Rate"
          value={metrics?.summary.matchRate || '0%'}
          subtitle="leads to requests"
          icon={<Target className="h-4 w-4 text-muted-foreground" />}
        />
        
        <StatsCard
          title="Conversion Rate"
          value={metrics?.summary.conversionRate || '0%'}
          subtitle="requests to jobs"
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
        />
      </div>
      
      {/* Enhanced Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Filters & Search</CardTitle>
          <CardDescription>
            Filter and drill down into your lead attribution data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Row 1: Search and Basic Filters */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search leads and requests..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value as FilterState['status'] }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Match Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Matches</SelectItem>
                  <SelectItem value="matched">Matched</SelectItem>
                  <SelectItem value="unmatched">Unmatched</SelectItem>
                </SelectContent>
              </Select>
              
              <Select
                value={filters.franchise || 'all'}
                onValueChange={(value: string) => setFilters(prev => ({ 
                  ...prev, 
                  franchise: value === 'all' ? undefined : (value as 'hamilton' | 'northyork')
                }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Franchise" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Franchises</SelectItem>
                  <SelectItem value="hamilton">Hamilton</SelectItem>
                  <SelectItem value="northyork">North York</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Advanced Filters */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <Select
                value={filters.requestStatus || 'all'}
                onValueChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  requestStatus: value === 'all' ? undefined : (value as FilterState['requestStatus'])
                }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Request Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="assessment_completed">Assessment Done</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.timeFrame || 'all'}
                onValueChange={(value) => setFilters(prev => ({ 
                  ...prev, 
                  timeFrame: value === 'all' ? undefined : (value as FilterState['timeFrame'])
                }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Time Frame" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="last30days">Last 30 Days</SelectItem>
                  <SelectItem value="last90days">Last 90 Days</SelectItem>
                  <SelectItem value="thisyear">This Year</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="Filter by city..."
                value={filters.city || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value || undefined }))}
                className="w-48"
              />

              <Input
                placeholder="Filter by source..."
                value={filters.source || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value || undefined }))}
                className="w-48"
              />
            </div>

            {/* Active Filters Display */}
            {(filters.requestStatus || filters.timeFrame || filters.city || filters.source || filters.drillDown) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {filters.drillDown && (
                  <Badge variant="secondary" className="gap-1">
                    <BarChart3 className="w-3 h-3" />
                    Drill-down: {filters.drillDown.type} = {filters.drillDown.value}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 ml-1"
                      onClick={() => setFilters(prev => ({ ...prev, drillDown: undefined }))}
                    >
                      ×
                    </Button>
                  </Badge>
                )}
                {filters.requestStatus && filters.requestStatus !== 'all' && (
                  <Badge variant="outline">Status: {filters.requestStatus}</Badge>
                )}
                {filters.timeFrame && filters.timeFrame !== 'all' && (
                  <Badge variant="outline">Time: {filters.timeFrame}</Badge>
                )}
                {filters.city && (
                  <Badge variant="outline">City: {filters.city}</Badge>
                )}
                {filters.source && (
                  <Badge variant="outline">Source: {filters.source}</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({
                    status: 'all',
                    franchise: undefined,
                    channel: undefined,
                    dateRange: undefined
                  })}
                  className="h-6 text-xs"
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Tabs defaultValue="requests" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6">
          <TabsTrigger value="requests">Requests ({requestsPagination.total})</TabsTrigger>
          <TabsTrigger value="leads">Leads ({leadsPagination.total})</TabsTrigger>
          <TabsTrigger value="matches">Matches ({matches.length})</TabsTrigger>
          <TabsTrigger value="franchise-analytics">Franchise Analytics</TabsTrigger>
          <TabsTrigger value="city-analytics">City Analytics</TabsTrigger>
          <TabsTrigger value="time-analytics">Time Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Jobber Service Requests</CardTitle>
              <CardDescription>
                Showing {requests.length} of {requestsPagination.total.toLocaleString()} service requests from Jobber CRM
              </CardDescription>
            </CardHeader>
            <CardContent>
              {requestsLoading ? (
                <div className="text-center py-8">Loading requests...</div>
              ) : (
                <>
                  <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request ID</TableHead>
                      <TableHead>Client Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Service Type</TableHead>
                      <TableHead>Franchise</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>
                          <div className="font-mono text-sm">{request.jobber_request_id}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{request.client_name || 'Unknown'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {request.client_email && (
                              <div className="text-sm flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {request.client_email}
                              </div>
                            )}
                            {request.client_phone && (
                              <div className="text-sm flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {request.client_phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{request.request_title || 'Service Request'}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{request.franchise || 'Unknown'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {request.jobber_created_at ? 
                              new Date(request.jobber_created_at).toLocaleDateString() : 
                              'Unknown'
                            }
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={request.status === 'completed' ? 'default' : 
                                   request.status === 'in_progress' ? 'outline' : 'secondary'}
                          >
                            {request.status || 'pending'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            TBD
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                  <PaginationControls 
                    pagination={requestsPagination}
                    onPageChange={handleRequestsPageChange}
                    onPageSizeChange={handleRequestsPageSizeChange}
                    isLoading={requestsLoading}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Website Lead Submissions</CardTitle>
              <CardDescription>
                Showing {leads.length} of {leadsPagination.total.toLocaleString()} lead submissions from website forms
              </CardDescription>
            </CardHeader>
            <CardContent>
              {leadsLoading ? (
                <div className="text-center py-8">Loading leads...</div>
              ) : (
                <>
                  <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead ID</TableHead>
                      <TableHead>Client Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Service Interest</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Match Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell>
                          <div className="font-mono text-sm">{lead.id}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{lead.client_name || 'Unknown'}</div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {lead.client_email && (
                              <div className="text-sm flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {lead.client_email}
                              </div>
                            )}
                            {lead.client_phone && (
                              <div className="text-sm flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {lead.client_phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {lead.service_street ? 
                              `${lead.service_street}` : 
                              'Not provided'
                            }
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {lead.submission_url ? 'Website Form' : 'Direct'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {lead.service_requested || lead.message?.substring(0, 50) + '...' || 'General Inquiry'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {lead.created_at ? 
                              new Date(lead.created_at).toLocaleDateString() : 
                              'Unknown'
                            }
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            Unmatched
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  </Table>
                  <PaginationControls 
                    pagination={leadsPagination}
                    onPageChange={handleLeadsPageChange}
                    onPageSizeChange={handleLeadsPageSizeChange}
                    isLoading={leadsLoading}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="matches" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Lead Attribution Results</CardTitle>
              <CardDescription>
                {filteredMatches.length} matches found
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead Info</TableHead>
                    <TableHead>Request ID</TableHead>
                    <TableHead>Match Score</TableHead>
                    <TableHead>Franchise</TableHead>
                    <TableHead>Job Value</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMatches.map((match) => (
                    <TableRow key={match.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{match.leads?.client_name || 'Unknown'}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            {match.leads?.client_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {match.leads.client_email}
                              </span>
                            )}
                            {match.leads?.client_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {match.leads.client_phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{match.jobber_request_id}</div>
                      </TableCell>
                      <TableCell>
                        <ConfidenceBadge score={match.match_score || 0} />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {match.franchise === 'hamilton' ? 'Hamilton' : 'North York'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {match.job_total ? `$${match.job_total.toLocaleString()}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            match.match_type === 'automatic' ? 'bg-green-100 text-green-800' :
                            match.match_type === 'manual' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }
                        >
                          {match.match_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedMatch(match)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="channels" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Object.entries(metrics?.byChannel || {}).map(([channel, channelMetrics]) => (
              <ChannelCard key={channel} name={channel} metrics={channelMetrics} />
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="locations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Geographic Performance</CardTitle>
              <CardDescription>
                Lead performance by postal Forward Sortation Area (FSA)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Postal FSA</TableHead>
                    <TableHead>Leads</TableHead>
                    <TableHead>Requests</TableHead>
                    <TableHead>Jobs</TableHead>
                    <TableHead>Conversion Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(metrics?.byLocation || {}).map(([fsa, locationMetrics]) => {
                    const conversionRate = locationMetrics.requests > 0 
                      ? ((locationMetrics.jobs / locationMetrics.requests) * 100).toFixed(1)
                      : '0'
                    
                    return (
                      <TableRow key={fsa}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <span className="font-mono">{fsa}</span>
                          </div>
                        </TableCell>
                        <TableCell>{locationMetrics.leads}</TableCell>
                        <TableCell>{locationMetrics.requests}</TableCell>
                        <TableCell>{locationMetrics.jobs}</TableCell>
                        <TableCell>
                          <span className="font-medium text-green-600">{conversionRate}%</span>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="franchise-analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Franchise Performance Analytics</CardTitle>
              <CardDescription>
                Detailed analytics breakdown by franchise with time-based trends
              </CardDescription>
              <div className="flex items-center gap-2 pt-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search franchises..."
                    value={analyticsSearch}
                    onChange={(e) => setAnalyticsSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAnalyticsSearch('')}
                  disabled={!analyticsSearch}
                >
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {franchiseAnalyticsLoading ? (
                <div className="text-center py-8">Loading franchise analytics...</div>
              ) : (
                <div className="space-y-6">
                  {franchiseAnalytics
                    .filter(franchise => 
                      !analyticsSearch || 
                      franchise.franchise.toLowerCase().includes(analyticsSearch.toLowerCase())
                    )
                    .map((franchise) => (
                    <Card key={franchise.franchise} className="p-4 cursor-pointer hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">{franchise.franchise.toUpperCase()}</h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFilters(prev => ({
                              ...prev,
                              franchise: franchise.franchise as 'hamilton' | 'northyork',
                              drillDown: {
                                type: 'franchise',
                                value: franchise.franchise,
                                parentFilters: { ...prev }
                              }
                            }))}
                            className="h-6 w-6 p-0"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Badge variant="outline">{franchise.totalRequests} requests</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold">{franchise.totalRequests}</div>
                          <div className="text-sm text-muted-foreground">Total Requests</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            ${franchise.totalValue.toLocaleString()}
                          </div>
                          <div className="text-sm text-muted-foreground">Total Value</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold">
                            ${Math.round(franchise.avgValue).toLocaleString()}
                          </div>
                          <div className="text-sm text-muted-foreground">Avg Value</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {franchise.conversionRate.toFixed(1)}%
                          </div>
                          <div className="text-sm text-muted-foreground">Conversion Rate</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h4 className="font-medium mb-2">Weekly Trends</h4>
                          <div className="space-y-1">
                            {franchise.timeAnalytics.weekly.slice(-4).map((week) => (
                              <div key={week.week} className="flex justify-between text-sm">
                                <span>{week.week}</span>
                                <span>{week.count} requests</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Monthly Trends</h4>
                          <div className="space-y-1">
                            {franchise.timeAnalytics.monthly.slice(-4).map((month) => (
                              <div key={month.month} className="flex justify-between text-sm">
                                <span>{month.month}</span>
                                <span>{month.count} requests</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Yearly Trends</h4>
                          <div className="space-y-1">
                            {franchise.timeAnalytics.yearly.map((year) => (
                              <div key={year.year} className="flex justify-between text-sm">
                                <span>{year.year}</span>
                                <span>{year.count} requests</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="city-analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Service City Analytics</CardTitle>
              <CardDescription>
                Request volume and value breakdown by service city
              </CardDescription>
              <div className="flex items-center gap-2 pt-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search cities..."
                    value={analyticsSearch}
                    onChange={(e) => setAnalyticsSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAnalyticsSearch('')}
                  disabled={!analyticsSearch}
                >
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {cityAnalyticsLoading ? (
                <div className="text-center py-8">Loading city analytics...</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>City</TableHead>
                      <TableHead>Province</TableHead>
                      <TableHead>Franchise</TableHead>
                      <TableHead>Total Requests</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Avg Value</TableHead>
                      <TableHead>Recent Activity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cityAnalytics
                      .filter(city => 
                        !analyticsSearch || 
                        city.city.toLowerCase().includes(analyticsSearch.toLowerCase()) ||
                        city.province.toLowerCase().includes(analyticsSearch.toLowerCase()) ||
                        city.franchise.toLowerCase().includes(analyticsSearch.toLowerCase())
                      )
                      .sort((a, b) => b.totalRequests - a.totalRequests)
                      .slice(0, 50)
                      .map((city) => (
                        <TableRow 
                          key={`${city.city}-${city.province}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setFilters(prev => ({
                            ...prev,
                            city: city.city,
                            drillDown: {
                              type: 'city',
                              value: `${city.city}, ${city.province}`,
                              parentFilters: { ...prev }
                            }
                          }))}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div>
                                <div className="font-medium">{city.city}</div>
                                <div className="text-sm text-muted-foreground">{city.postalCode}</div>
                              </div>
                              <BarChart3 className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </TableCell>
                          <TableCell>{city.province}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{city.franchise}</Badge>
                          </TableCell>
                          <TableCell>{city.totalRequests}</TableCell>
                          <TableCell>
                            <span className="font-medium text-green-600">
                              ${city.totalValue.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            ${Math.round(city.totalValue / city.totalRequests).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {city.timeAnalytics.monthly.slice(-1)[0]?.count || 0} this month
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time-analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Time-Based Analytics</CardTitle>
              <CardDescription>
                Request volume and trends over time periods
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Weekly Performance</h3>
                  <div className="space-y-2">
                    {franchiseAnalytics[0]?.timeAnalytics.weekly.slice(-8).map((week) => (
                      <div key={week.week} className="flex justify-between items-center">
                        <span className="text-sm">{week.week}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{week.count}</span>
                          <div 
                            className="h-2 bg-blue-500 rounded"
                            style={{
                              width: `${(week.count / Math.max(...(franchiseAnalytics[0]?.timeAnalytics.weekly.map(w => w.count) || [1]))) * 60}px`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Monthly Performance</h3>
                  <div className="space-y-2">
                    {franchiseAnalytics[0]?.timeAnalytics.monthly.slice(-6).map((month) => (
                      <div key={month.month} className="flex justify-between items-center">
                        <span className="text-sm">{month.month}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{month.count}</span>
                          <div 
                            className="h-2 bg-green-500 rounded"
                            style={{
                              width: `${(month.count / Math.max(...(franchiseAnalytics[0]?.timeAnalytics.monthly.map(m => m.count) || [1]))) * 60}px`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="font-semibold mb-4">Yearly Performance</h3>
                  <div className="space-y-2">
                    {franchiseAnalytics[0]?.timeAnalytics.yearly.map((year) => (
                      <div key={year.year} className="flex justify-between items-center">
                        <span className="text-sm">{year.year}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{year.count}</span>
                          <div 
                            className="h-2 bg-purple-500 rounded"
                            style={{
                              width: `${(year.count / Math.max(...(franchiseAnalytics[0]?.timeAnalytics.yearly.map(y => y.count) || [1]))) * 60}px`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}